import React, {
  useEffect,
} from 'react';
import MailIcon from '@mui/icons-material/MailOutlineRounded';
import IconButton from '@mui/material/IconButton';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import {
  SidePanelComponentProps,
} from 'pages/Dashboard/contexts/sidePanel';
import {
  formatMoney,
  isNilOrZero,
} from '@populateui/utils';
import SummaryItem from 'views/EMR/Billing/SummaryItem';
import {
  useGetBillingSummary,
} from 'views/EMR/hooks/billing';
import Button from 'pages/Dashboard/components/Button';
import {
  billingEventChannel,
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useGetPatientInfo,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  usePatientInvoiceData,
} from 'pages/Dashboard/pages/Billing/hooks';
import ServiceBreakdownView from 'pages/Dashboard/pages/Billing/ServiceBreakdownView';
import CollectIcon from 'core/icons/collectIcon';
import PaymentSuccessIcon from 'core/icons/paymentSuccess';
import WriteOffIcon from 'core/icons/writeoffIcon';
import ToolTip from 'core/components/Tooltip';
import PrinterIcon from 'core/icons/printer';
import Row from 'pages/Dashboard/components/Row';
import {
  Patient,
} from 'pages/Dashboard/types/patient';
import {
  PatientDTO,
} from 'dtos';

export default function PatientInvoiceInfo({ entity }: SidePanelComponentProps) {
  // TODO: read visit date from the encounter summary once the API returns it, then drop the visitDate URL param
  const { patientId, encounterId, visitDate } = entity;

  const {
    data: patient,
  } = useGetPatientInfo(patientId);
  const { data: summary, refetch } = useGetBillingSummary({ encounterId, patientId });
  const {
    data: invoiceData,
    isLoading: isLoadingInvoice,
    refetch: refetchInvoice,
  } = usePatientInvoiceData({
    patientId: patientId ?? 0,
    encounterIds: [encounterId],
  });

  const { [encounterId]: statement } = invoiceData?.visitNotesForStatement ?? {};
  const hasInvoiceData = !isNil(statement) && !isEmpty(statement) && !isLoadingInvoice;

  useEffect(() => {
    const unsubscribe = paymentsEventChannel.on('onPatientBalanceChange', async () => {
      await Promise.all([refetch(), refetchInvoice()]);
    });

    return () => {
      unsubscribe();
    };
  }, [patientId, encounterId]);

  return (
    <div className="flex flex-col gap-4 overflow-hidden">
      <div className="flex gap-4 px-4 pt-4">
        <SummaryItem
          value={summary?.due}
          title="Appointment due"
        />
        <SummaryItem
          value={summary?.totalBalance}
          title="Total balance overdue"
        />
        <SummaryItem
          value={summary?.credit}
          title="Patient's credit"
        />
      </div>
      <div className="flex w-full justify-evenly items-center p-4 gap-4">
        <Button
          variant="outlined"
          color="primary"
          className="!w-full !max-w-[unset] flex gap-2 !border-blue-500"
          onClick={() => {
            paymentsEventChannel.emit('onPaymentDialogOpen', {
              patient: patient as Partial<PatientDTO>,
              encounterId: encounterId!,
            });
          }}
        >
          <CollectIcon className="!w-5 !h-5" />
          Collect
        </Button>
        <Button
          variant="outlined"
          color="primary"
          className="!w-full !max-w-[unset] flex gap-2 !border-blue-500"
          disabled={isNilOrZero(patient?.patientId)}
          onClick={() => {
            billingEventChannel.emit('onEncountersAllocation', { patient: patient as Partial<PatientDTO> });
          }}
        >
          <PaymentSuccessIcon className="!w-5 !h-5" />
          Allocate
        </Button>
        <Button
          variant="outlined"
          color="primary"
          className="!w-full !max-w-[unset] !border-blue-500 flex gap-2"
          onClick={() => {
            paymentsEventChannel.emit('onAddWriteoff', {
              patient: patient!,
              encounterId: encounterId!,
              visitDateTime: visitDate!,
            });
          }}
        >
          <WriteOffIcon className="!w-5 !h-5" />
          Write Off
        </Button>
      </div>
      {hasInvoiceData && (
        <div className="flex-1 flex flex-col px-4 gap-1 overflow-y-auto">
          <Row
            title="Patient's Statement"
            className="!border-0 !p-0"
          >
            <div className="flex gap-2">
              <ToolTip
                title="Print Patient Statement"
              >
                <span>
                  <IconButton
                    aria-label="Print Patient Statement"
                    color="info"
                    onClick={() => {
                      paymentsEventChannel.emit('onStatementDialog', {
                        type: 'print',
                        patient: patient as Patient,
                        visits: [encounterId ?? 0],
                      });
                    }}
                    className="disabled:opacity-50"
                  >
                    <PrinterIcon className="!w-4 !h-4 !text-inherit" />
                  </IconButton>
                </span>
              </ToolTip>
              <ToolTip
                title="Send Patient Statement"
              >
                <span>
                  <IconButton
                    aria-label="Send Patient Statement"
                    color="info"
                    onClick={() => {
                      paymentsEventChannel.emit('onStatementDialog', {
                        type: 'send',
                        patient: patient as Patient,
                        visits: [encounterId ?? 0],
                      });
                    }}
                    className="disabled:opacity-50"
                  >
                    <MailIcon className="!w-4 !h-4 !text-inherit" />
                  </IconButton>
                </span>
              </ToolTip>
            </div>
          </Row>
          <div className="flex flex-col w-full border rounded text-xs font-semibold populate-breakdown-table max-h-[450px]">
            <div className="px-4 py-2 bg-gray-100 flex justify-between">
              <div className="flex justify-between flex-auto">
                <p>
                  Billed:
                  {' '}
                  {formatMoney(statement?.patientBalance?.totalBilled ?? 0)}
                </p>
                <p>
                  Write Off:
                  {' '}
                  {formatMoney(statement?.patientBalance?.totalEncounterPatientWriteOff ?? 0)}
                </p>
                <p>
                  Paid:
                  {' '}
                  {formatMoney(statement?.patientBalance?.billPaid ?? 0)}
                </p>
                <p>
                  Due:
                  {' '}
                  {formatMoney(statement?.patientBalance?.totalDue ?? 0)}
                </p>
              </div>
            </div>
            <ServiceBreakdownView data={statement?.allocation ?? []} />
          </div>
        </div>
      )}
    </div>
  );
}
